import { ACTION_STATUSES, TRANSACTION_TYPES } from 'shared/consts';
import { BookingStatus } from 'store/bookings/types';
import { ReceiptContainer, ButtonContainer, DoublePane, LeftPane, RightPane } from '../styles';
import React from 'react';
import { PatchTransaction, TransactionDetails } from 'store/transactions/types';
import { useIntl } from 'react-intl';
import { ReceiptOverview } from './receipt-overview';
import { OrderedAddOns, useOrderedAddOnsVisible } from 'components/ordered-add-ons';
import { ReceiptSummary } from './receipt-summary';
import { ReceiptCheckIn } from './receipt-check-in';
import { ActionBarClearance, ReceiptTemperatureVote } from './styles';
import { OrderType } from 'store/cart/types';
import { useBookingObject } from 'components/payment/receipt/components/hooks/useBookingObject.hook';
import { DoneButtonBlock } from 'components/payment/receipt/components/done-button';
import { UnlockDorsLink } from 'components/payment/receipt/components/unlock-dors-link';
import { isBookingActionable } from 'components/payment/receipt/components/utils/booking-actionability.util';

interface SwipeReceiptContentProps {
  transaction: TransactionDetails;
  onCancelBookingClick: VoidFunction;
  patchTransactionData: Pick<PatchTransaction, 'status' | 'type'>;
  isSwipeModalContent: boolean;
  onCheckInClick: VoidFunction;
  onCloseClick: VoidFunction;
  isFullscreen?: boolean;
  isActivityPane?: boolean;
}

export const SwipeReceiptContent = ({
  isSwipeModalContent,
  onCancelBookingClick,
  patchTransactionData,
  transaction,
  onCheckInClick,
  onCloseClick,
  isFullscreen = false,
  isActivityPane = false,
}: SwipeReceiptContentProps) => {
  const intl = useIntl();
  const status = transaction?.details?.status;
  // A past booking's transaction status never changes once it has ended, and a cancelled/denied
  // booking is already terminal — in both cases check-in and cancel no longer apply (see the util).
  const bookingIsActionable = isBookingActionable(transaction);
  /* One decision, two uses: the receipt drops the vendor lines because this renders them. */
  const hasOrderedAddOns = useOrderedAddOnsVisible(transaction);

  // RES-1231: check-in must not be offered until the booking is confirmed (Accepted).
  const isCheckInAllowed =
    status === BookingStatus.ACCEPTED &&
    !!transaction?.details?.check_in?.status &&
    patchTransactionData.status !== ACTION_STATUSES.FULFILLED &&
    bookingIsActionable;

  const mappedBookingObject = useBookingObject(transaction);

  return (
    <ReceiptContainer data-testid="receipt-swipe-modal" isFullscreen={isFullscreen}>
      {transaction && (
        <>
          <DoublePane isFullscreen={isFullscreen} isActivityPane={isActivityPane}>
            <LeftPane className="left-pane" isFullscreen={isFullscreen}>
              <ReceiptOverview isSwipeModalContent={isSwipeModalContent} transaction={transaction} />
              {isCheckInAllowed && <ReceiptCheckIn onCheckIn={onCheckInClick} />}
              {!!transaction?.details?.resource_id && (
                <ReceiptTemperatureVote
                  resourceId={String(transaction.details.resource_id)}
                  startAt={transaction.details.start_at}
                  endAt={transaction.details.end_at}
                  checkInStatus={transaction.details.check_in?.status}
                />
              )}
              <UnlockDorsLink isSwipeModalContent={isSwipeModalContent} transaction={transaction} />
            </LeftPane>
            <RightPane className="right-pane" isFullscreen={isFullscreen}>
              <ReceiptSummary
                onCancelBooking={bookingIsActionable ? onCancelBookingClick : undefined}
                isSwipeModalContent={isSwipeModalContent}
                hideVendorAddOns={hasOrderedAddOns}
                removeBottomSpacing={hasOrderedAddOns || isActivityPane}
                transaction={mappedBookingObject}
              />
              {/*
                ⛔ Its own block, not the receipt's payment details. Vendor add-ons never enter
                `total_price`, and that block is gated on it -- so on a free room a $50 add-on
                appeared nowhere at all. See `OrderedAddOns`.
              */}
              <ActionBarClearance hasActionBar={!isActivityPane} isSwipeModalContent={isSwipeModalContent}>
                <OrderedAddOns transaction={transaction} />
              </ActionBarClearance>
            </RightPane>
          </DoublePane>
          {/*
           * VIS-2032: the activity-center pane is a persistent detail view, not a dismissible modal —
           * there is nothing to close there, so the Done footer is dropped (marker-gated). The receipt
           * scrolls to fill the pane instead. Phone and desktop keep their Done control.
           */}
          {!isActivityPane && (
            <ButtonContainer className="button-container" data-testid="double-pane-button">
              {(transaction?.type === TRANSACTION_TYPES.RESOURCES_BOOKING ||
                mappedBookingObject?.type === OrderType.RESOURCE_BOOKING) &&
                !mappedBookingObject.isMultiday && (
                  <DoneButtonBlock onClick={onCloseClick} text={intl.formatMessage({ id: 'done' })} />
                )}
            </ButtonContainer>
          )}
        </>
      )}
    </ReceiptContainer>
  );
};
